import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { formatUnifiedPrice } from '../../utils/priceCalculatorUnified';
import { PRICING_CONFIG } from '../../config/pricingConfig';
import { getTokenAuth } from '../../utils/authUtils';
import { getStripePromise, reloadStripePromise } from '../../utils/stripeLoader';

// Options de style pour CardElement
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false
};

// Composant principal du formulaire de paiement
const StripePaymentForm = ({ 
  reservationData, 
  breakdown, 
  onSuccess, 
  onError, 
  onCancel,
  loading: parentLoading = false,
  onProcessingChange
}) => {
  const { t } = useTranslation(['common', 'booking', 'listing']);
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [priceDetailsOpen, setPriceDetailsOpen] = useState(true); // Ouvert par défaut
  
  // État pour le consentement off-session (pour les réservations avec caution)
  const [offSessionConsent, setOffSessionConsent] = useState(false);
  const [showConsentDetails, setShowConsentDetails] = useState(false);
  const hasDeposit = breakdown?.deposit_amount > 0;
  const isIncludedPricing = breakdown?.pricing_display_mode === 'included';
  const displayedClientTotal = breakdown?.client_total || ((breakdown?.rental_amount || 0) + (breakdown?.service_fee || 0) + (breakdown?.tax_amount || 0));
  const displayedSubtotal = isIncludedPricing
    ? displayedClientTotal
    : ((breakdown?.rental_amount || 0) + (breakdown?.service_fee || 0));
  
  // Calculer delivery fee pour l'exclure du net price
  const deliveryFeeCents = reservationData?.extra_fees?.delivery_fee_cents || 0;
  const deliveryFee = deliveryFeeCents / 100;
  const netPrice = displayedClientTotal - (breakdown?.service_fee || 0) - (breakdown?.tax_amount || 0) - deliveryFee;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe n\'est pas encore chargé. Veuillez réessayer.');
      return;
    }

    if (!cardComplete) {
      setError('Veuillez remplir correctement les informations de la carte.');
      return;
    }

    // Vérifier le consentement off-session si caution présente
    if (hasDeposit && !offSessionConsent) {
      setError(t('booking:payment.stripe.offSessionConsent.required'));
      return;
    }

    setLoading(true);
    onProcessingChange?.(true);
    setError('');

    try {
      // 1. Créer PaymentMethod avec les données de la carte
      const cardElement = elements.getElement(CardElement);
      
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: reservationData.renterName || 'Client Krillou',
          email: reservationData.renterEmail
        }
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      console.log('✅ PaymentMethod créé:', paymentMethod.id);

      // Vérifier le token avant l'envoi
      const token = getTokenAuth();
      console.log('🔐 [StripePaymentModal] Token status:', {
        tokenExists: !!token,
        tokenLength: token ? token.length : 0,
        tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
        tokenValid: token && token.length > 10 && token.includes('.')
      });

      if (!token || token.length < 10) {
        throw new Error('Token d\'authentification manquant ou invalide');
      }

      // 2. Appeler notre endpoint intégré /with-stripe
      const response = await fetch('/api/v1/reservations/with-stripe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          // Données de réservation
          listing_id: reservationData.listing_id,
          start_date: reservationData.start_date,
          end_date: reservationData.end_date,
          start_at: reservationData.start_at,
          end_at: reservationData.end_at,
          pickup_address: reservationData.pickup_address,
          return_address: reservationData.return_address,
          guest_count: reservationData.guest_count,
          quantity_requested: reservationData.quantity_requested,
          seats_requested: reservationData.seats_requested,
          special_requests: reservationData.special_requests,
          // Prix & variantes (commandes produit / time_slot pricing override)
          total_price: reservationData.total_price,
          deposit_amount: reservationData.deposit_amount,
          variant_selections: reservationData.variant_selections,
          // Extra fees (delivery fees)
          extra_fees: reservationData.extra_fees || null,
          
          // Données de paiement Stripe RÉELLES
          payment_method_id: paymentMethod.id,
          save_payment_method: saveCard,
          
          // Consentement off-session pour la caution (DSP2)
          off_session_consent: hasDeposit ? offSessionConsent : false
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erreur lors de la création de la réservation');
      }

      console.log('✅ Réservation créée avec succès:', result);

      // Vérifier si la réponse est valide
      if (!result.success || !result.data) {
        throw new Error('Réponse invalide du serveur');
      }

      const { data } = result;

      // 🛂 SCA / 3DS: si le backend indique requires_action, terminer la confirmation via Stripe.js
      if (data?.payment?.requires_action && Array.isArray(data?.payment?.stripe_actions)) {
        for (const action of data.payment.stripe_actions) {
          if (!action?.client_secret) {
            throw new Error('Paiement nécessite une action (SCA) mais aucun client_secret n\'a été fourni');
          }

          const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(action.client_secret);

          if (confirmError) {
            throw new Error(confirmError.message || 'Échec de confirmation 3DS');
          }

          if (paymentIntent && paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'requires_capture') {
            // requires_capture est OK pour un dépôt en manual capture
            throw new Error(`Statut de paiement inattendu après 3DS: ${paymentIntent.status}`);
          }
        }
      }

      // Le paiement a été traité avec succès par Stripe dans le backend
      console.log('🎉 Paiement et réservation traités avec succès');

      // 4. Callback de succès avec toutes les données
      onSuccess({
        reservation: data.reservation,
        payment: data.payment,
        calculation: data.calculation,
        codes: data.codes,
        documents: data.documents
      });

    } catch (err) {
      console.error('❌ Erreur lors du paiement:', err);
      
      // Essayer de parser la réponse d'erreur pour extraire les détails Stripe
      let structuredError = err;
      
      if (typeof err.message === 'string' && err.message.includes('lastPaymentError')) {
        try {
          // Chercher les détails JSON dans le message d'erreur
          const match = err.message.match(/\{.*lastPaymentError.*\}/);
          if (match) {
            const parsed = JSON.parse(match[0]);
            structuredError = parsed;
          }
        } catch (parseError) {
          console.warn('Could not parse error details:', parseError);
        }
      }
      
      setError(err.message);
      onError(structuredError);
    } finally {
      setLoading(false);
      onProcessingChange?.(false);
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  // Désactiver si loading parent ou local
  const isDisabled = loading || parentLoading;

  return (
    <div className="stripe-payment-form">
      <div className="payment-summary">
        <div
          className="payment-summary-header"
          onClick={() => setPriceDetailsOpen(!priceDetailsOpen)}
          style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'nowrap'
          }}
        >
          <h3 style={{ margin: 0, paddingRight: '8px', whiteSpace: 'nowrap' }}>{t('booking:payment.stripe.title')}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 120, justifyContent: 'flex-end' }}>
            <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#1e293b', marginLeft: '12px' }}>
              {formatUnifiedPrice(displayedClientTotal, breakdown?.currency || 'EUR')}
            </span>
            <span style={{ fontSize: '1.2rem', transform: priceDetailsOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
              ▼
            </span>
          </div>
        </div>
        
        <div className={`breakdown ${priceDetailsOpen ? 'open' : 'closed'}`}>
          {/* Dates (aligné avec récapitulatif cash) */}
          {reservationData?.start_date && (
            <div className="line-item" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
              <span style={{ fontWeight: 600 }}>{t('booking:sidebar.form.dates', 'Dates')}</span>
              <span style={{ textAlign: 'right' }}>
                {(() => {
                  try {
                    const s = reservationData.start_date.substring(0, 10);
                    const e = reservationData.end_date?.substring(0, 10);
                    const sd = parseISO(s);
                    const ed = e ? parseISO(e) : null;
                    if (!isValid(sd)) return `${s}${ed ? ` - ${e}` : ''}`;
                    return ed && isValid(ed)
                      ? `${format(sd, 'dd/MM/yyyy', { locale: fr })} - ${format(ed, 'dd/MM/yyyy', { locale: fr })}`
                      : format(sd, 'dd/MM/yyyy', { locale: fr });
                  } catch { return reservationData.start_date.substring(0, 10); }
                })()}
              </span>
            </div>
          )}
          {/* Check-in / Check-out — propriétés immobilières */}
          {reservationData?.start_at && reservationData?.end_at && (breakdown?.days ?? 0) > 0 && (
                  <div className="line-item" style={{ color: '#6200ee', fontSize: '0.9em', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
              <span style={{ fontWeight: 600 }}>🏠 {t('listing:property.section', 'Check-in / Check-out')}</span>
                    <span style={{ textAlign: 'right', fontWeight: 600 }}>
                {reservationData.start_at.substring(11, 16)} → {reservationData.end_at.substring(11, 16)}
                    </span>
                  </div>
                )}
          <div className="line-item">
            {(() => {
              if ((breakdown?.days ?? 0) > 0) {
                return <span>{t('booking:sidebar.form.subtitle', 'Réservation')} ({breakdown.days} {t('common:days')})</span>;
              }
              // If days === 0, show time-slot interval when available
              if (reservationData?.start_at && reservationData?.end_at) {
                try {
                  const s = parseISO(reservationData.start_at);
                  const e = parseISO(reservationData.end_at);
                  if (isValid(s) && isValid(e)) {
                    return <span>{t('booking:sidebar.form.subtitle', 'Réservation')} ({format(s, 'dd/MM/yyyy HH:mm', { locale: fr })} → {format(e, 'HH:mm', { locale: fr })})</span>;
                  }
                } catch (err) {
                  // fallthrough
                }
              }
              return <span>{t('booking:sidebar.form.subtitle', 'Réservation')}</span>;
            })()}
            <span>{formatUnifiedPrice(breakdown?.rental_amount || 0, breakdown?.currency || 'EUR')}</span>
          </div>
          {/* Quantité demandée (toujours affichée) */}
          <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
            <span>{t('booking:sidebar.form.quantity', 'Quantité')}</span>
            {(() => {
              const qty = reservationData?.quantity_requested || 1;
              return (
                <span>{qty} {t('booking:sidebar.form.unit', { count: qty })}</span>
              );
            })()}
          </div>
          {/* Participants / capacité (time_slot_capacity) */}
          {reservationData?.guest_count != null && (
            <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
              <span>{t('booking:sidebar.form.guestCount.label', 'Participants')}</span>
              <span>{reservationData.guest_count}</span>
            </div>
          )}
          {/* Places réservées (time_slot_capacity) */}
          {reservationData?.seats_requested != null && (
            <div className="line-item" style={{ color: '#555', fontSize: '0.9em' }}>
              <span>{t('booking:sidebar.form.seatsLabel', 'Places à réserver')}</span>
              <span>{reservationData.seats_requested}</span>
            </div>
          )}
          <div className="line-item">
            <span>{t('products:order.cart.netPrice','Prix net')}</span>
            <span>{formatUnifiedPrice(netPrice, breakdown?.currency || 'EUR')}</span>
          </div>
          
          {/* Ligne livraison ou retrait */}
          {(() => {
            const extraFees = reservationData?.extra_fees;
            const isDelivery = extraFees?.delivery_fee_cents != null;
            const deliveryFeeCents = extraFees?.delivery_fee_cents || 0;
            const deliveryType = extraFees?.delivery_type || 'standard';
            
            if (isDelivery) {
              // Mode livraison
              return (
                <div className="line-item" style={{ color: '#6200ee', fontSize: '0.95em' }}>
                  <span>
                    {deliveryType === 'express' ? '⚡ ' : '🚚 '}
                    {deliveryType === 'express' 
                      ? t('products:order.deliveryFee.express', 'Livraison express')
                      : t('products:order.deliveryFee.standard', 'Livraison standard')}
                  </span>
                  <span>
                    {deliveryFeeCents > 0 
                      ? formatUnifiedPrice(deliveryFeeCents / 100, breakdown?.currency || 'EUR')
                      : t('products:order.deliveryFee.free', 'GRATUITE')}
                  </span>
                </div>
              );
            } else {
              // Mode retrait sur place
              return (
                <div className="line-item" style={{ color: '#666', fontSize: '0.9em' }}>
                  <span>{t('products:order.pickup.free', 'Retrait sur place')}</span>
                  <span>{t('products:order.deliveryFee.free', 'Gratuit')}</span>
                </div>
              );
            }
          })()}
          
          <div className="line-item">
            <span>{t('common:serviceFee')} ({breakdown?.commission_rate_percent ?? PRICING_CONFIG.SERVICE_FEE_PERCENT}%)</span>
            <span>{formatUnifiedPrice(breakdown?.service_fee || 0, breakdown?.currency || 'EUR')}</span>
          </div>
          {/* TVA (sur les frais de service Krillou) */}
          {(breakdown?.tax_amount > 0) && (
            <div className="line-item" style={{ color: '#888', fontSize: '0.9em', fontStyle: 'italic' }}>
              {(() => { const vr = breakdown?.vat_rate_percent ?? PRICING_CONFIG.TAX_PERCENT; return <span>{t('booking:sidebar.pricing.tax', { percent: vr, defaultValue: `dont TVA (${vr}%)` })}</span>; })()}
              <span>{formatUnifiedPrice(breakdown.tax_amount, breakdown?.currency || 'EUR')}</span>
            </div>
          )}
          
          {breakdown?.deposit_amount > 0 && (
            <div className="line-item">
              <span>{t('booking:payment.stripe.deposit')}</span>
              <span>{formatUnifiedPrice(breakdown.deposit_amount, breakdown?.currency || 'EUR')}</span>
            </div>
          )}
          <div className="line-item total">
            <span><strong>{t('booking:payment.stripe.total')}</strong></span>
            <span><strong>{formatUnifiedPrice(displayedClientTotal, breakdown?.currency || 'EUR')}</strong></span>
          </div>
          
          {breakdown?.deposit_amount > 0 && (
            <div className="deposit-notice" style={{ 
              marginTop: '10px', 
              padding: '8px', 
              backgroundColor: '#f0f8ff', 
              borderRadius: '4px', 
              fontSize: '0.9em',
              color: '#666'
            }}>
              💡 {t('booking:payment.stripe.depositRefundNotice', "La caution sera intégralement remboursée à l'issue de la prestation, en l'absence de dommage constaté.")}
            </div>
          )}
          {/* Lieu (aligné avec récapitulatif cash) */}
          {reservationData?.pickup_address && (
            <div style={{ 
              marginTop: '10px',
              paddingTop: '8px',
              borderTop: '1px solid #e2e8f0',
              fontSize: '0.85em',
              color: '#555'
            }}>
              <span style={{ fontWeight: 600 }}>📍 {t('booking:sidebar.payment.addresses', 'Lieu')} :</span>{' '}
              <span>{reservationData.pickup_address}</span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="payment-form">
        <div className="form-group">
          <label htmlFor="card-element">
            {t('booking:payment.stripe.cardNumber')}
          </label>
          <div className="card-element-container">
            <CardElement
              id="card-element"
              options={cardElementOptions}
              onChange={handleCardChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={saveCard}
              onChange={(e) => setSaveCard(e.target.checked)}
              disabled={isDisabled}
            />
            {t('booking:payment.stripe.saveCard', 'Enregistrer cette carte pour les futurs paiements')}
          </label>
        </div>

        {/* Protection de la caution (obligatoire) */}
        {hasDeposit && (
          <div className="form-group off-session-consent" style={{
            marginTop: '15px',
            padding: '12px 14px',
            backgroundColor: '#f0f9f4',
            border: '1px solid #86cfac',
            borderRadius: '8px'
          }}>
            <div style={{ marginBottom: '8px', fontWeight: '600', color: '#276749', fontSize: '0.9em' }}>
              {t('booking:payment.stripe.offSessionConsent.title', '🛡️ Protection de votre caution')}
            </div>
            <label className="checkbox-label" style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '10px',
              cursor: 'pointer',
              padding: '8px 10px',
              backgroundColor: offSessionConsent ? '#e6f4ea' : '#fff',
              borderRadius: '6px',
              border: offSessionConsent ? '2px solid #34a853' : '2px solid #ddd',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="checkbox"
                checked={offSessionConsent}
                onChange={(e) => setOffSessionConsent(e.target.checked)}
                disabled={isDisabled}
                required={true}
                style={{ marginTop: '2px', minWidth: '18px', minHeight: '18px', accentColor: '#34a853' }}
              />
              <span style={{ fontSize: '0.9em', color: '#333', fontWeight: offSessionConsent ? '500' : '400' }}>
                {t('booking:payment.stripe.offSessionConsent.checkbox', 'J\'accepte les conditions de protection de la caution')}
                <span style={{ color: '#e53935', marginLeft: '4px' }}>*</span>
              </span>
            </label>
            <button
              type="button"
              onClick={() => setShowConsentDetails(v => !v)}
              style={{
                marginTop: '8px',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: '#276749',
                fontSize: '0.8em',
                fontWeight: 500,
                textDecoration: 'underline',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {showConsentDetails
                ? t('booking:payment.stripe.offSessionConsent.detailsHide', '▲ Masquer les détails')
                : t('booking:payment.stripe.offSessionConsent.detailsShow', '▼ Voir les détails juridiques')}
            </button>
            {showConsentDetails && (
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #c3e6cb' }}>
                <p style={{ fontSize: '0.85em', color: '#444', marginTop: 0, marginBottom: '8px', lineHeight: '1.5' }}>
                  {t('booking:payment.stripe.offSessionConsent.explanation',
                    { depositAmount: formatUnifiedPrice(breakdown?.deposit_amount || 0, breakdown?.currency || 'EUR') })}
                </p>
                <p style={{ fontSize: '0.85em', color: '#444', marginTop: 0, marginBottom: '8px', lineHeight: '1.5' }}>
                  {t('booking:payment.stripe.offSessionConsent.disclaimer')}{' '}
                  <RouterLink
                    to="/terms#deposits"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#276749', fontWeight: 600, textDecoration: 'underline' }}
                  >
                    {t('booking:payment.stripe.offSessionConsent.legalLinkLabel')}
                  </RouterLink>
                </p>
                <p style={{ fontSize: '0.78em', color: '#666', margin: 0, lineHeight: '1.5' }}>
                  <span style={{ color: '#34a853' }}>✓</span>{' '}
                  {t('booking:payment.stripe.offSessionConsent.legalNotice',
                    'Conforme DSP2 • Aucun prélèvement sans justificatif • Contestation possible 13 mois')}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isDisabled}
          >
            {t('booking:payment.stripe.buttons.cancel')}
          </button>
          
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isDisabled || !cardComplete || (hasDeposit && !offSessionConsent)}
          >
            {isDisabled ? (
              <>
                <span className="spinner"></span>
                {t('booking:payment.stripe.processing')}
              </>
            ) : (
              <>
                🔒 {t('booking:payment.stripe.buttons.pay', { amount: formatUnifiedPrice(displayedClientTotal, breakdown?.currency || 'EUR') })}
              </>
            )}
          </button>
        </div>
      </form>

      <div className="security-info">
        <div className="security-badge">
          {t('booking:payment.stripe.securePayment', '🔒 Paiement sécurisé par Stripe')}
        </div>
        <p className="security-text">
          {t('booking:payment.stripe.securityInfo', 'Vos données de paiement sont cryptées et sécurisées. Nous n\'enregistrons jamais vos informations de carte bancaire.')}
        </p>
      </div>
    </div>
  );
};

// Composant wrapper avec Elements Provider
const StripePaymentModal = (props) => {
  const { t } = useTranslation('booking');
  const [stripePromise, setStripePromise] = useState(() => getStripePromise());
  const [stripeUnavailable, setStripeUnavailable] = useState(false);

  useEffect(() => {
    let mounted = true;
    stripePromise.then((stripe) => {
      if (!mounted) return;
      setStripeUnavailable(!stripe);
    });

    return () => {
      mounted = false;
    };
  }, [stripePromise]);

  const handleRetryStripe = () => {
    setStripeUnavailable(false);
    setStripePromise(reloadStripePromise());
  };

  if (stripeUnavailable) {
    return (
      <div className="modal-overlay" onClick={props.onCancel}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header" style={{
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white'
          }}>
            <h2 style={{ color: 'white' }}>{t('booking:payment.stripe.modalTitle', '💳 Finaliser votre réservation') }</h2>
            <button
              className="close-button"
              onClick={props.onCancel}
              type="button"
              aria-label={t('common:close', 'Fermer')}
              style={{ color: 'white' }}
            >
              ✕
            </button>
          </div>
          <div className="modal-body">
            <div className="stripe-payment-error">
              <div className="error-content">
                <h3>{t('booking:payment.stripe.errorTitle', 'Erreur de paiement')}</h3>
                <p>{t('booking:payment.stripe.stripeUnavailable', 'Impossible de charger Stripe pour le moment. Vérifiez votre connexion puis réessayez.')}</p>
                <div className="error-actions">
                  <button className="btn-secondary" onClick={props.onCancel}>
                    {t('booking:payment.stripe.buttons.cancel', 'Annuler')}
                  </button>
                  <button className="btn-primary" onClick={handleRetryStripe}>
                    {t('booking:payment.stripe.buttons.retry', 'Réessayer')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <Elements stripe={stripePromise}>
      <div className="modal-overlay" onClick={props.onCancel}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header" style={{
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white'
          }}>
            <h2 style={{ color: 'white' }}>{t('booking:payment.stripe.modalTitle', '💳 Finaliser votre réservation') }</h2>
            <button 
              className="close-button" 
              onClick={props.onCancel}
              type="button"
              aria-label={t('common:close', 'Fermer')}
              style={{ color: 'white' }}
            >
              ✕
            </button>
          </div>
          <div className="modal-body">
            <StripePaymentForm {...props} />
          </div>
        </div>
      </div>
    </Elements>
  );
};

export default StripePaymentModal;
